import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Calendar, MapPin, Users, Award, Mail, Menu, X, CalendarPlus, ExternalLink, FileText, Download } from 'lucide-react';
import axios from 'axios';

// Use relative URL for API calls - works in both preview and production
// This ensures the frontend always calls the same domain it's hosted on
const BACKEND_URL = '';

// Scientific Programme PDF URL
const PROGRAMME_PDF_URL = 'https://customer-assets.emergentagent.com/job_e4b88294-5f0b-47df-967b-fd4bb3d24a04/artifacts/fo6n5aru_WhatsApp%20Image%202026-06-15%20at%2020.44.37.jpeg';

// Countdown Timer Component
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const targetDate = new Date('2026-06-28T08:00:00');

    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex justify-center gap-3 md:gap-6">
      <div className="text-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 md:px-6 md:py-4 border border-white/20">
          <span className="text-2xl md:text-5xl font-bold text-white">{timeLeft.days}</span>
        </div>
        <p className="text-xs md:text-sm text-gray-300 mt-2">Days</p>
      </div>
      <div className="text-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 md:px-6 md:py-4 border border-white/20">
          <span className="text-2xl md:text-5xl font-bold text-white">{timeLeft.hours}</span>
        </div>
        <p className="text-xs md:text-sm text-gray-300 mt-2">Hours</p>
      </div>
      <div className="text-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 md:px-6 md:py-4 border border-white/20">
          <span className="text-2xl md:text-5xl font-bold text-white">{timeLeft.minutes}</span>
        </div>
        <p className="text-xs md:text-sm text-gray-300 mt-2">Minutes</p>
      </div>
      <div className="text-center">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 md:px-6 md:py-4 border border-white/20">
          <span className="text-2xl md:text-5xl font-bold text-white">{timeLeft.seconds}</span>
        </div>
        <p className="text-xs md:text-sm text-gray-300 mt-2">Seconds</p>
      </div>
    </div>
  );
};

// Hero Background Slider Component
const heroImages = [
  'https://images.unsplash.com/photo-1765801122549-2c11dc9cbd78?w=1920&q=80', // Dubai Marina night lights reflection
  'https://images.unsplash.com/photo-1677508397947-b477be938a9b?w=1920&q=80', // Atlantis The Palm Dubai
  'https://images.unsplash.com/photo-1542544499-bce9dc3bb4e8?w=1920&q=80', // Dubai Festival City waterfront night
];

const HeroSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {heroImages.map((image, index) => (
        <div
          key={index}
          className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
          style={{
            backgroundImage: `url(${image})`,
            opacity: index === currentIndex ? 1 : 0,
          }}
        />
      ))}
      {/* Gradient overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-gray-900/40 via-gray-900/20 to-gray-900/40"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 via-transparent to-gray-900/10"></div>
    </>
  );
};

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    hospital: '',
    country: '',
    email: '',
    phoneCode: '+971',
    phone: '',
    registrationDays: '2',
    selectedDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [agendaDay, setAgendaDay] = useState(1);

  // Google Calendar link
  const addToCalendarUrl = 'https://www.google.com/calendar/render?action=TEMPLATE&text=AURA%202026%20-%20Advanced%20Ultrasound%20Regional%20Anaesthesia%20Conference&dates=20260628T080000Z/20260629T180000Z&location=InterContinental%20Dubai%20Festival%20City%2C%20Dubai%2C%20UAE&details=Join%20leading%20experts%20in%20ultrasound-guided%20regional%20anaesthesia%20and%20pain%20management.%20Register%20at%20https%3A%2F%2Faura2026.com';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const countries = [
    "United Arab Emirates", "Saudi Arabia", "Kuwait", "Qatar", "Bahrain", "Oman",
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia",
    "Australia", "Austria", "Azerbaijan", "Bahamas", "Bangladesh", "Barbados", "Belarus",
    "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana",
    "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon",
    "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile", "China", "Colombia",
    "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic",
    "Denmark", "Djibouti", "Dominica", "Dominican Republic", "East Timor", "Ecuador",
    "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Ethiopia",
    "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana",
    "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti",
    "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland",
    "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati",
    "North Korea", "South Korea", "Kosovo", "Kyrgyzstan", "Laos", "Latvia", "Lebanon",
    "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Macedonia",
    "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands",
    "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia",
    "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal",
    "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "Norway", "Pakistan",
    "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines",
    "Poland", "Portugal", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis",
    "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino",
    "Sao Tome and Principe", "Senegal", "Serbia", "Seychelles", "Sierra Leone",
    "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa",
    "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Swaziland", "Sweden",
    "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Togo",
    "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
    "Uganda", "Ukraine", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
    "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
  ];

  const countryCodes = [
    { code: '+971', country: 'UAE' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+965', country: 'Kuwait' },
    { code: '+974', country: 'Qatar' },
    { code: '+973', country: 'Bahrain' },
    { code: '+968', country: 'Oman' },
    { code: '+91', country: 'India' },
    { code: '+92', country: 'Pakistan' },
    { code: '+880', country: 'Bangladesh' },
    { code: '+44', country: 'UK' },
    { code: '+1', country: 'US/Canada' },
    { code: '+61', country: 'Australia' },
    { code: '+60', country: 'Malaysia' },
    { code: '+65', country: 'Singapore' },
    { code: '+63', country: 'Philippines' },
    { code: '+20', country: 'Egypt' },
    { code: '+27', country: 'South Africa' },
    { code: '+33', country: 'France' },
    { code: '+49', country: 'Germany' },
    { code: '+39', country: 'Italy' },
    { code: '+34', country: 'Spain' },
    { code: '+81', country: 'Japan' },
    { code: '+86', country: 'China' },
    { code: '+82', country: 'South Korea' },
    { code: '+62', country: 'Indonesia' },
    { code: '+66', country: 'Thailand' },
    { code: '+84', country: 'Vietnam' },
    { code: '+64', country: 'New Zealand' },
    { code: '+94', country: 'Sri Lanka' },
    { code: '+977', country: 'Nepal' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email and phone before proceeding
    if (!formData.email || !formData.email.trim()) {
      toast.error("Email address is required.");
      return;
    }
    if (!formData.phone || !formData.phone.trim()) {
      toast.error("Phone number is required.");
      return;
    }
    if (formData.registrationDays === '1' && !formData.selectedDate) {
      toast.error("Please select your preferred date.");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Combine phone code and phone number
      const submissionData = {
        ...formData,
        phone: `${formData.phoneCode} ${formData.phone}`,
        registrationDays: parseInt(formData.registrationDays),
        selectedDate: formData.registrationDays === '2' ? 'Both Days' : formData.selectedDate,
        amount: formData.registrationDays === '2' ? 1200 : 800
      };
      // Remove phoneCode from submission
      delete submissionData.phoneCode;
      
      const response = await axios.post(`${BACKEND_URL}/api/registrations/`, submissionData);
      
      if (response.data.success) {
        // Registration saved successfully, now redirect to Stripe payment
        const amount = submissionData.amount;
        toast.success(`Redirecting to payment (AED ${amount})...`, {
          description: "Please complete your payment to confirm registration.",
          duration: 3000,
        });
        
        // Redirect to appropriate Stripe payment link
        const stripeUrl = amount === 800
          ? (process.env.REACT_APP_STRIPE_PAYMENT_URL_1DAY || 'https://buy.stripe.com/9B614ogaj04f2Uu2Bp3VC01')
          : (process.env.REACT_APP_STRIPE_PAYMENT_URL || 'https://buy.stripe.com/aFacN60bleZ90Mm4Jx3VC00');
        
        setTimeout(() => {
          window.location.href = stripeUrl;
        }, 1500);
      } else {
        throw new Error(response.data.message || 'Submission failed');
      }
    } catch (error) {
      let errorMessage = "Something went wrong. Please try again.";
      
      if (error.response) {
        // Server responded with error
        if (error.response.status === 409) {
          errorMessage = "This email is already registered.";
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        // Network error
        errorMessage = "Network error. Please check your connection.";
      }
      
      toast.error("Error", {
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_e4b88294-5f0b-47df-967b-fd4bb3d24a04/artifacts/pa0oq57d_AuraLogo2026.png" 
                alt="AURA 2026 Logo" 
                className="h-12 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-[#05ab95] transition-colors font-medium">
                About
              </button>
              <button onClick={() => scrollToSection('agenda')} className="text-gray-700 hover:text-[#05ab95] transition-colors font-medium">
                Programme
              </button>
              <button onClick={() => scrollToSection('team')} className="text-gray-700 hover:text-[#05ab95] transition-colors font-medium">
                Conference Team
              </button>
              <button onClick={() => scrollToSection('faculty')} className="text-gray-700 hover:text-[#05ab95] transition-colors font-medium">
                Faculty
              </button>
              <button onClick={() => scrollToSection('sponsors')} className="text-gray-700 hover:text-[#05ab95] transition-colors font-medium">
                Sponsors
              </button>
            </nav>
            
            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
              <div className="flex flex-col space-y-3">
                <button 
                  onClick={() => { scrollToSection('about'); setMobileMenuOpen(false); }} 
                  className="text-gray-700 hover:text-[#05ab95] transition-colors font-medium text-left py-2"
                >
                  About
                </button>
                <button 
                  onClick={() => { scrollToSection('agenda'); setMobileMenuOpen(false); }} 
                  className="text-gray-700 hover:text-[#05ab95] transition-colors font-medium text-left py-2"
                >
                  Programme
                </button>
                <button 
                  onClick={() => { scrollToSection('team'); setMobileMenuOpen(false); }} 
                  className="text-gray-700 hover:text-[#05ab95] transition-colors font-medium text-left py-2"
                >
                  Conference Team
                </button>
                <button 
                  onClick={() => { scrollToSection('faculty'); setMobileMenuOpen(false); }} 
                  className="text-gray-700 hover:text-[#05ab95] transition-colors font-medium text-left py-2"
                >
                  Faculty
                </button>
                <button 
                  onClick={() => { scrollToSection('sponsors'); setMobileMenuOpen(false); }} 
                  className="text-gray-700 hover:text-[#05ab95] transition-colors font-medium text-left py-2"
                >
                  Sponsors
                </button>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Image Slider - extends behind the card */}
        <div className="absolute inset-0">
          <HeroSlider />
        </div>
        
        {/* Hero title area */}
        <div className="relative z-10 pt-24 md:pt-36 pb-8">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 leading-tight drop-shadow-lg">
                AURA 2026
              </h1>
              <p className="text-2xl md:text-4xl text-gray-100 font-light drop-shadow-md">
                Advanced Ultrasound Regional Anaesthesia & Pain Conference
              </p>
            </div>
          </div>
        </div>

        {/* White card overlapping - hero image continues behind */}
        <div className="relative z-20 container mx-auto px-4 pt-8 pb-12">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 md:p-10 max-w-5xl mx-auto border border-gray-100">
            
            {/* Conference details */}
            <div>
              <p className="text-gray-500 text-sm font-medium uppercase tracking-wider mb-3">
                InterContinental Dubai Festival City, Dubai, UAE
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-snug">
                Two Days of Cutting-Edge Education, Hands-On Training & Networking
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Join leading experts in ultrasound-guided regional anaesthesia and pain management at one of the region's most anticipated academic conferences.
              </p>

              {/* Highlight badges in grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#05ab95]" />
                  <span className="text-gray-800 font-semibold text-sm">June 28-29, 2026</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#05ab95]" />
                  <span className="text-gray-800 font-semibold text-sm">12 DHA CME Points</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#05ab95]" />
                  <span className="text-gray-800 font-semibold text-sm">Oral Poster Presentation</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#05ab95]" />
                  <span className="text-gray-800 font-semibold text-sm">International Faculty</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => setShowRegistrationModal(true)} 
                  size="lg"
                  className="bg-[#05ab95] hover:bg-[#048c7a] text-white px-8 py-5 text-base shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-register-btn"
                >
                  Register Now
                </Button>
                <Button 
                  onClick={() => window.open(addToCalendarUrl, '_blank')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-5 text-base transition-all"
                  data-testid="hero-calendar-btn"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Add to Calendar
                </Button>
              </div>
            </div>

            {/* ESON - bottom right, subtle - desktop only */}
            <div className="hidden md:flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
              <span className="text-gray-400 text-xs leading-tight text-right">
                Supported by<br />Emirati Society Of Anesthesiology
              </span>
              <img 
                src="https://customer-assets.emergentagent.com/job_e4b88294-5f0b-47df-967b-fd4bb3d24a04/artifacts/v0php4fc_esonlogo.jpeg" 
                alt="Emirati Society of Anesthesiology" 
                className="w-10 h-12 object-contain"
              />
            </div>

            {/* Mobile: ESON shown below */}
            <div className="flex md:hidden items-center justify-center gap-3 mt-4 pt-4 border-t border-gray-200">
              <img 
                src="https://customer-assets.emergentagent.com/job_e4b88294-5f0b-47df-967b-fd4bb3d24a04/artifacts/v0php4fc_esonlogo.jpeg" 
                alt="Emirati Society of Anesthesiology" 
                className="w-12 h-14 object-contain"
              />
              <span className="text-gray-700 text-sm font-medium leading-tight">
                Supported by<br />Emirati Society Of Anesthesiology
              </span>
            </div>
          </div>
        </div>
      </section>


      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              About AURA 2026
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600">
              <p className="mb-6 text-lg leading-relaxed">
                AURA 2026 is a premier international conference dedicated to advancing the field of ultrasound-guided regional anaesthesia and pain medicine. This two-day intensive program brings together world-renowned faculty and healthcare professionals from across the globe. Originally scheduled for April 4-5, 2026, the conference will now be held on June 28-29, 2026.
              </p>
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Award className="w-5 h-5 text-[#05ab95] mr-2" />
                    For Professionals
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Anaesthesiologists</li>
                    <li>• Pain Physicians</li>
                    <li>• Intensivists</li>
                    <li>• Residents & Fellows</li>
                    <li>• Acute Pain Teams</li>
                    <li>• Perioperative Physicians</li>
                    <li>• Allied Healthcare Professionals</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Users className="w-5 h-5 text-[#05ab95] mr-2" />
                    What to Expect
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Expert lectures and presentations</li>
                    <li>• Hands-on scanning stations</li>
                    <li>• Interactive case discussions</li>
                    <li>• Latest techniques and innovations</li>
                    <li>• Oral Poster Presentation</li>
                    <li>• Networking opportunities</li>
                    <li>• International faculty</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scientific Agenda Section */}
      <section id="agenda" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Scientific Agenda
            </h2>
            <p className="text-gray-500 mb-8">InterContinental Dubai Festival City | June 28-29, 2026</p>

            {/* Day Tabs */}
            <div className="flex gap-3 mb-8">
              <button
                onClick={() => setAgendaDay(1)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  agendaDay === 1
                    ? 'bg-[#05ab95] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="agenda-day1-tab"
              >
                Day 1 — Lectures
              </button>
              <button
                onClick={() => setAgendaDay(2)}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${
                  agendaDay === 2
                    ? 'bg-[#05ab95] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="agenda-day2-tab"
              >
                Day 2 — Hands-on Training
              </button>
            </div>

            {/* Day 1 Content */}
            {agendaDay === 1 && (
              <div className="space-y-0">
                <div className="bg-[#05ab95] text-white text-center py-3 rounded-full mb-6 font-semibold">
                  Morning Session
                </div>
                <div className="space-y-4 mb-8">
                  {[
                    { time: '08:30 – 09:00', title: 'Delegate Registration' },
                    { time: '09:00 – 09:30', title: 'Welcome Speech', speaker: 'Dr. Sheelaj Sharma, Scientific Programme Chair' },
                    { time: '09:30 – 10:00', title: 'Ultrasound physics and injection techniques', speaker: 'Dr. Ravi Kare' },
                    { time: '10:00 – 10:30', title: 'Chest Wall Blocks / Role in Pain Relief after Breast Surgery & Fracture Ribs', speaker: 'Dr. Florian Paturi' },
                    { time: '10:30 – 10:45', title: 'Masterclass — Chest Wall Blocks', type: 'masterclass' },
                    { time: '10:45 – 11:15', title: 'Spinal Sonography – I', speaker: 'Dr. Ravi Kare (UAE)' },
                    { time: '11:15 – 11:30', title: 'Masterclass — Lumbar Sonography', type: 'masterclass' },
                    { time: '11:30 – 11:45', title: 'Coffee Break', type: 'break' },
                    { time: '11:45 – 12:15', title: 'ESP Block — Drug Dynamics and How it Works', speaker: 'Dr. Sheelaj Sharma (UAE)' },
                    { time: '12:15 – 12:30', title: 'Masterclass — ESP Block', type: 'masterclass' },
                    { time: '12:30 – 01:15', title: 'HIP Surgery — Role of Regional Anaesthesia vs. LIA — Better Outcome', speaker: 'Dr. Amit Verma' },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-start gap-4 ${item.type === 'break' ? 'py-3' : ''}`}>
                      <span className="text-sm text-gray-400 font-mono w-28 flex-shrink-0 pt-0.5">{item.time}</span>
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${
                          item.type === 'break' ? 'bg-gray-300' : item.type === 'masterclass' ? 'bg-yellow-400' : 'bg-[#05ab95]'
                        }`} />
                        <div>
                          <p className={`font-medium ${item.type === 'break' ? 'text-gray-400 italic' : 'text-gray-900'}`}>{item.title}</p>
                          {item.speaker && <p className="text-sm text-gray-500 mt-0.5">{item.speaker}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-gray-100 text-gray-500 text-center py-3 rounded-full mb-6 font-medium italic">
                  01:15 – 02:15 — Lunch
                </div>
                <div className="bg-[#05ab95] text-white text-center py-3 rounded-full mb-6 font-semibold">
                  Afternoon Session
                </div>
                <div className="space-y-4">
                  {[
                    { time: '02:15 – 02:45', title: 'Regional Anaesthesia in Obese Patients — Technical Challenges & Benefits', speaker: 'Dr. Harry Thompson (UAE)' },
                    { time: '02:45 – 03:15', title: 'Phrenic Nerve Sparing Blocks for Shoulder Analgesia', speaker: 'Dr. Sheelaj Sharma (UAE)' },
                    { time: '03:15 – 03:30', title: 'Masterclass — Suprascapular Nerve (Anterior & Posterior)', type: 'masterclass' },
                    { time: '03:30 – 04:00', title: 'Coffee Break', type: 'break' },
                    { time: '04:00 – 04:30', title: 'Nerve Blocks for Knee Joints — Acute & Chronic Pain: Technical Challenges & Benefits', speaker: 'Dr. Shravan Kumar Tirunagari (UAE)' },
                    { time: '04:30 – 05:00', title: 'Palliative Care Approach to Cancer Pain', speaker: 'Dr. Neil Nijhawan (UAE)' },
                    { time: '05:00', title: 'Closing Remarks and Discussion' },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-start gap-4 ${item.type === 'break' ? 'py-3' : ''}`}>
                      <span className="text-sm text-gray-400 font-mono w-28 flex-shrink-0 pt-0.5">{item.time}</span>
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${
                          item.type === 'break' ? 'bg-gray-300' : item.type === 'masterclass' ? 'bg-yellow-400' : 'bg-[#05ab95]'
                        }`} />
                        <div>
                          <p className={`font-medium ${item.type === 'break' ? 'text-gray-400 italic' : 'text-gray-900'}`}>{item.title}</p>
                          {item.speaker && <p className="text-sm text-gray-500 mt-0.5">{item.speaker}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Day 2 Content */}
            {agendaDay === 2 && (
              <div className="space-y-0">
                <div className="bg-[#05ab95] text-white text-center py-3 rounded-full mb-6 font-semibold">
                  Morning Session
                </div>
                <div className="space-y-4 mb-8">
                  {[
                    { time: '09:00 – 09:30', title: 'Delegates & Faculty Registration' },
                    { time: '09:30 – 09:45', title: 'Welcome Address', speaker: 'Dr. Sheelaj Sharma' },
                    { time: '09:45 – 11:00', title: 'Scanning Stations 1 & 2', type: 'scanning' },
                    { time: '11:00 – 11:15', title: 'Coffee Break', type: 'break' },
                    { time: '11:15 – 01:15', title: 'Scanning Stations 3 & 4', type: 'scanning' },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-start gap-4 ${item.type === 'break' ? 'py-3' : ''}`}>
                      <span className="text-sm text-gray-400 font-mono w-28 flex-shrink-0 pt-0.5">{item.time}</span>
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${
                          item.type === 'break' ? 'bg-gray-300' : item.type === 'scanning' ? 'bg-blue-400' : 'bg-[#05ab95]'
                        }`} />
                        <div>
                          <p className={`font-medium ${item.type === 'break' ? 'text-gray-400 italic' : 'text-gray-900'}`}>{item.title}</p>
                          {item.speaker && <p className="text-sm text-gray-500 mt-0.5">{item.speaker}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-gray-100 text-gray-500 text-center py-3 rounded-full mb-6 font-medium italic">
                  01:15 – 02:15 — Lunch
                </div>
                <div className="bg-[#05ab95] text-white text-center py-3 rounded-full mb-6 font-semibold">
                  Afternoon Session
                </div>
                <div className="space-y-4 mb-10">
                  {[
                    { time: '02:15 – 03:45', title: 'Scanning Stations 5 & 6', type: 'scanning' },
                    { time: '03:45 – 04:00', title: 'Coffee Break', type: 'break' },
                    { time: '04:00 – 04:45', title: 'Scanning Station 7', type: 'scanning' },
                    { time: '04:45 – 05:00', title: 'Feedback & Closure' },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-start gap-4 ${item.type === 'break' ? 'py-3' : ''}`}>
                      <span className="text-sm text-gray-400 font-mono w-28 flex-shrink-0 pt-0.5">{item.time}</span>
                      <div className="flex items-start gap-3 flex-1">
                        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${
                          item.type === 'break' ? 'bg-gray-300' : item.type === 'scanning' ? 'bg-blue-400' : 'bg-[#05ab95]'
                        }`} />
                        <div>
                          <p className={`font-medium ${item.type === 'break' ? 'text-gray-400 italic' : 'text-gray-900'}`}>{item.title}</p>
                          {item.speaker && <p className="text-sm text-gray-500 mt-0.5">{item.speaker}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4">Scanning Stations</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {[
                      { num: 1, desc: 'Upper Limb (Above Clavicle) — Interscalene / Supraclavicular / Suprascapular Nerve Blocks' },
                      { num: 2, desc: 'Upper Limb (Below Clavicle) — Axillary / Infraclavicular / Forearm Block' },
                      { num: 3, desc: 'Chest Wall Blocks — PEC 1&2, Serratus Anterior Plane & ESP Block' },
                      { num: 4, desc: 'Abdominal Blocks — Rectus Sheath / TAP / QL Block' },
                      { num: 5, desc: 'Lower Limb — Femoral / Adductor Canal / Popliteal Sciatic / iPACK / Genicular' },
                      { num: 6, desc: 'Lumbar Spinal Sonography — Spinal / Epidural' },
                      { num: 7, desc: 'Needling Station' },
                    ].map((s) => (
                      <div key={s.num} className="flex items-start gap-2">
                        <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded flex-shrink-0">S{s.num}</span>
                        <span className="text-gray-600">{s.desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Download Programme Link */}
            <div className="mt-10 text-center">
              <a 
                href={PROGRAMME_PDF_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#05ab95] hover:text-[#048c7a] font-medium transition-colors"
                data-testid="download-programme-link"
              >
                <Download className="w-4 h-4" />
                Download Full Programme
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Directors Section - Conference Secretariat */}
      <section id="team" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              Conference Secretariat
            </h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              Meet the experts leading this premier conference
            </p>
            <div className="grid md:grid-cols-2 gap-12">
              {/* Course Director */}
              <div className="text-center">
                <div className="mb-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_anaesthesia-conf/artifacts/w3sntvyn_Roopa.png" 
                    alt="Dr. Roopa Chatterjee" 
                    className="w-48 h-48 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                  />
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-100">
                  <span className="inline-block bg-[#05ab95] text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                    Course Director
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Dr. Roopa Chatterjee
                  </h3>
                  <p className="text-gray-600 mb-1">
                    Consultant in Pain Medicine & Anaesthesia
                  </p>
                  <p className="text-gray-500">
                    North Middlesex University Hospital, London, UK
                  </p>
                </div>
              </div>

              {/* Scientific Programme Chair */}
              <div className="text-center">
                <div className="mb-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_anaesthesia-conf/artifacts/pv31i637_scientific%20chair%20photo.jpeg" 
                    alt="Dr. Sheelaj Sharma" 
                    className="w-48 h-48 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                  />
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm border-2 border-gray-100">
                  <span className="inline-block bg-[#05ab95] text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                    Scientific Programme Chair
                  </span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Dr. Sheelaj Sharma
                  </h3>
                  <p className="text-gray-600 mb-1">
                    Consultant Anaesthetist
                  </p>
                  <p className="text-gray-500">
                    United Arab Emirates
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Faculty Team Section */}
      <section id="faculty" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              Distinguished Faculty
            </h2>
            <p className="text-center text-gray-600 mb-12 text-lg">
              Distinguished experts bringing advanced training and clinical insights
            </p>
            
            {/* First Row - Faculty with Photographs (3 cards) */}
            {/* First Row - 5 Faculty Members (Alphabetical by surname) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              {/* Dr Masood Ahmed */}
              <div className="text-center">
                <div className="mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_0dadb0f2-68f4-45a4-8c6b-092df69739a1/artifacts/usdgbu08_Dr%20Masood%20Ahmed.png" 
                    alt="Dr Masood Ahmed" 
                    className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                  />
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                  <h3 className="text-base font-bold text-gray-900 mb-1">
                    Dr Masood Ahmed
                  </h3>
                  <p className="text-gray-600 mb-1 text-xs">
                    Consultant Anaesthetist
                  </p>
                  <p className="text-gray-500 text-xs">
                    Sheikh Shakbout Medical City, Abu Dhabi, UAE
                  </p>
                </div>
              </div>

              {/* Dr Mohammed Al Harbi */}
              <div className="text-center">
                <div className="mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_0dadb0f2-68f4-45a4-8c6b-092df69739a1/artifacts/c0gifwiw_Dr%20Harbi.png" 
                    alt="Dr Mohammed Al Harbi" 
                    className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                  />
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                  <h3 className="text-base font-bold text-gray-900 mb-1">
                    Dr Mohammed Al Harbi FRCPC
                  </h3>
                  <p className="text-gray-600 mb-1 text-xs">
                    Associate Professor & Chairman of Anaesthesia Department
                  </p>
                  <p className="text-gray-500 text-xs">
                    King Abdulaziz Medical City, Riyadh, KSA
                  </p>
                </div>
              </div>

              {/* Dr Shailendra Chaudhuri */}
              <div className="text-center">
                <div className="mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_e4b88294-5f0b-47df-967b-fd4bb3d24a04/artifacts/9nxv4kmd_15.png" 
                    alt="Dr Shailendra Chaudhuri" 
                    className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                  />
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                  <h3 className="text-base font-bold text-gray-900 mb-1">
                    Dr Shailendra Chaudhuri
                  </h3>
                  <p className="text-gray-600 mb-1 text-xs">
                    Consultant Anaesthetist
                  </p>
                  <p className="text-gray-500 text-xs">
                    UAE
                  </p>
                </div>
              </div>

              {/* Dr Ravi Kare */}
              <div className="text-center">
                <div className="mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_0dadb0f2-68f4-45a4-8c6b-092df69739a1/artifacts/qglap0r1_Ravi%20Kare.png" 
                    alt="Dr Ravi Kare" 
                    className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                  />
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                  <h3 className="text-base font-bold text-gray-900 mb-1">
                    Dr Ravi Kare
                  </h3>
                  <p className="text-gray-600 mb-1 text-xs">
                    Consultant Anaesthetist and Chronic Pain
                  </p>
                  <p className="text-gray-500 text-xs">
                    Healthpoint Hospital, Abu Dhabi, UAE
                  </p>
                </div>
              </div>
            </div>

            {/* Row 2 - Lequeux, Nijhawan, Paturi, Thompson */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              {/* Dr Pierre-Yves Lequeux */}
              <div className="text-center">
                <div className="mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_0dadb0f2-68f4-45a4-8c6b-092df69739a1/artifacts/ogq5ay71_14.png" 
                    alt="Dr Pierre-Yves Lequeux" 
                    className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                  />
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                  <h3 className="text-base font-bold text-gray-900 mb-1">
                    Dr Pierre-Yves Lequeux
                  </h3>
                  <p className="text-gray-600 mb-1 text-xs">
                    Consultant Anaesthetist
                  </p>
                  <p className="text-gray-500 text-xs">
                    Sheikh Khalifa Medical City, Abu Dhabi, UAE
                  </p>
                </div>
              </div>

              {/* Dr Neil Nijhawan */}
              <div className="text-center">
                <div className="mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_0dadb0f2-68f4-45a4-8c6b-092df69739a1/artifacts/ttsmxemx_Neil%20Nijahwan.png" 
                    alt="Dr Neil Nijhawan" 
                    className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                  />
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                  <h3 className="text-base font-bold text-gray-900 mb-1">
                    Dr Neil Nijhawan
                  </h3>
                  <p className="text-gray-600 mb-1 text-xs">
                    Consultant/HOD - Pain & Palliative
                  </p>
                  <p className="text-gray-500 text-xs">
                    Burjeel Medical City, Abu Dhabi, UAE
                  </p>
                </div>
              </div>

                {/* Dr Florian Paturi */}
                <div className="text-center">
                  <div className="mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_e4b88294-5f0b-47df-967b-fd4bb3d24a04/artifacts/ef5mlp50_Florian.png" 
                      alt="Dr Florian Paturi" 
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                    />
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      Dr Florian Paturi
                    </h3>
                    <p className="text-gray-600 mb-1 text-xs">
                      Consultant Anaesthetist
                    </p>
                    <p className="text-gray-500 text-xs">
                      UAE
                    </p>
                  </div>
                </div>

                {/* Dr Harry Thompson */}
                <div className="text-center">
                  <div className="mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_0dadb0f2-68f4-45a4-8c6b-092df69739a1/artifacts/1c68i0zc_Harry%20Thompson.png" 
                      alt="Dr Harry Thompson" 
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                    />
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      Dr Harry Thompson
                    </h3>
                    <p className="text-gray-600 mb-1 text-xs">
                      Consultant Anaesthetist
                    </p>
                    <p className="text-gray-500 text-xs">
                      Cleveland Hospital, UAE
                    </p>
                  </div>
                </div>
            </div>

            {/* Row 3 - Tirunagari, Upadhyay, Verma, Wadhwani */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                {/* Dr Shravan Kumar Tirunagari */}
                <div className="text-center">
                  <div className="mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_e4b88294-5f0b-47df-967b-fd4bb3d24a04/artifacts/h9zpf7hh_Dr.%20Shravan%20Kumar%20Tirunagari.png" 
                      alt="Dr Shravan Kumar Tirunagari" 
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                    />
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      Dr Shravan Kumar Tirunagari
                    </h3>
                    <p className="text-gray-600 mb-1 text-xs">
                      Consultant Anaesthetist
                    </p>
                    <p className="text-gray-500 text-xs">
                      UAE
                    </p>
                  </div>
                </div>

                {/* Dr Surjya Upadhyay */}
                <div className="text-center">
                  <div className="mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_0dadb0f2-68f4-45a4-8c6b-092df69739a1/artifacts/dxcrtrt3_Surjya%20Upadhyay.png" 
                      alt="Dr Surjya Upadhyay" 
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                    />
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      Dr Surjya Upadhyay
                    </h3>
                    <p className="text-gray-600 mb-1 text-xs">
                      Division Head of Anaesthesia and Critical Care
                    </p>
                    <p className="text-gray-500 text-xs">
                      NMC Royal Hospital, Dubai, UAE
                    </p>
                  </div>
                </div>

                {/* Dr Amit Verma */}
                <div className="text-center">
                  <div className="mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_e4b88294-5f0b-47df-967b-fd4bb3d24a04/artifacts/59zf6yvl_Auara_Dr%20Amit%20Verma.png" 
                      alt="Dr Amit Verma" 
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                    />
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      Dr Amit Verma
                    </h3>
                    <p className="text-gray-600 mb-1 text-xs">
                      Consultant Anaesthesia
                    </p>
                    <p className="text-gray-500 text-xs">
                      UAE
                    </p>
                  </div>
                </div>

                {/* Dr Jatendar Wadhwani */}
                <div className="text-center">
                  <div className="mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_0dadb0f2-68f4-45a4-8c6b-092df69739a1/artifacts/vg0a3mtu_Jatendar%20Wadhwani.png" 
                      alt="Dr Jatendar Wadhwani" 
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-[#05ab95]/20 shadow-lg"
                    />
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-gray-100">
                    <h3 className="text-base font-bold text-gray-900 mb-1">
                      Dr Jatendar Wadhwani
                    </h3>
                    <p className="text-gray-600 mb-1 text-xs">
                      Specialist Anaesthetist
                    </p>
                    <p className="text-gray-500 text-xs">
                      King's College Hospital, Dubai, UAE
                    </p>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Message from the Course Director */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 text-center">
              Welcome Message
            </h2>
            <h3 className="text-xl text-[#05ab95] font-semibold mb-10 text-center">
              Message from the Course Director
            </h3>
            
            <div className="bg-gradient-to-br from-gray-50 to-white p-8 md:p-12 rounded-lg border-2 border-gray-100">
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-5">
                  It is my great pleasure to welcome you to AURA – Advanced Ultrasound-Guided Regional Anaesthesia Course (AURA 2026), to be held in the UAE.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed mb-5">
                  AURA 2026 is a focused academic platform for clinicians committed to advancing excellence in regional anaesthesia and pain management. The programme emphasises evidence-based practice, patient safety, and practical clinical decision-making in contemporary pain care.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed mb-5">
                  Participants will benefit from a carefully curated programme featuring live masterclasses, hands-on ultrasound scanning, and interactive workshops covering ultrasound-guided techniques, perioperative and postoperative pain management, and evolving approaches to regional anaesthesia.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed mb-5">
                  The course brings together an experienced faculty of pain and anaesthesia specialists from the UAE and the UK, who will guide participants through both theoretical discussions and practical training.
                </p>
                
                <p className="text-lg text-gray-700 leading-relaxed">
                  We look forward to welcoming you to AURA 2026 for an engaging, immersive, and clinically relevant learning experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Section - CTA */}
      <section id="register" className="py-20 bg-gradient-to-br from-[#05ab95] to-[#048c7a]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Join AURA 2026?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Register now and secure your spot at the premier ultrasound-guided regional anaesthesia conference
            </p>
            
            {/* Conference Schedule & Fees */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-6 md:px-10 py-8 mb-8 max-w-2xl mx-auto border border-white/20">
              
              {/* Schedule */}
              <p className="text-sm text-white/70 uppercase tracking-wider mb-4 font-medium">Conference Schedule</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-white/10 rounded-lg p-4 border border-white/15">
                  <p className="text-white font-bold text-lg">Day 1</p>
                  <p className="text-white/90 text-sm">28 June 2026, Sunday</p>
                  <p className="text-white/80 text-sm mt-1">Lectures</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4 border border-white/15">
                  <p className="text-white font-bold text-lg">Day 2</p>
                  <p className="text-white/90 text-sm">29 June 2026, Monday</p>
                  <p className="text-white/80 text-sm mt-1">Hands-on Training</p>
                </div>
              </div>

              {/* Fees */}
              <p className="text-sm text-white/70 uppercase tracking-wider mb-3 font-medium">Registration Fees</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white/15 rounded-lg p-4 text-center">
                  <p className="text-white/80 text-sm">One Day</p>
                  <p className="text-white text-3xl font-bold">800 <span className="text-lg">AED</span></p>
                </div>
                <div className="bg-white/15 rounded-lg p-4 text-center border-2 border-white/30">
                  <p className="text-white/80 text-sm">Both Days</p>
                  <p className="text-white text-3xl font-bold">1,200 <span className="text-lg">AED</span></p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => setShowRegistrationModal(true)}
                size="lg"
                className="bg-white text-[#05ab95] hover:bg-gray-100 px-10 py-6 text-lg font-semibold shadow-xl"
              >
                Register Now
              </Button>
              <Button 
                onClick={() => scrollToSection('agenda')}
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#05ab95] px-10 py-6 text-lg"
              >
                <FileText className="w-5 h-5 mr-2" />
                View Agenda
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegistrationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Register for AURA 2026</h3>
                <p className="text-sm text-gray-600 mt-1">Fill in your details to proceed</p>
              </div>
              <button 
                onClick={() => setShowRegistrationModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-gray-700 font-medium mb-2 block">
                    Full Name *
                  </Label>
                  <Input 
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="border-gray-300 focus:border-[#05ab95] focus:ring-[#05ab95]"
                    placeholder="Dr. John Smith"
                  />
                </div>

                <div>
                  <Label htmlFor="designation" className="text-gray-700 font-medium mb-2 block">
                    Designation/Role *
                  </Label>
                  <Input 
                    id="designation"
                    name="designation"
                    type="text"
                    required
                    value={formData.designation}
                    onChange={handleChange}
                    className="border-gray-300 focus:border-[#05ab95] focus:ring-[#05ab95]"
                    placeholder="e.g., Consultant Anaesthetist"
                  />
                </div>

                <div>
                  <Label htmlFor="hospital" className="text-gray-700 font-medium mb-2 block">
                    Hospital/Institution *
                  </Label>
                  <Input 
                    id="hospital"
                    name="hospital"
                    type="text"
                    required
                    value={formData.hospital}
                    onChange={handleChange}
                    className="border-gray-300 focus:border-[#05ab95] focus:ring-[#05ab95]"
                    placeholder="Your hospital or institution"
                  />
                </div>

                <div>
                  <Label htmlFor="country" className="text-gray-700 font-medium mb-2 block">
                    Country *
                  </Label>
                  <select
                    id="country"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleChange}
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#05ab95] focus:ring-2 focus:ring-[#05ab95] focus:outline-none"
                  >
                    <option value="">Select your country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">
                    Email Address *
                  </Label>
                  <Input 
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="border-gray-300 focus:border-[#05ab95] focus:ring-[#05ab95]"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-gray-700 font-medium mb-2 block">
                    Phone Number *
                  </Label>
                  <div className="flex gap-2">
                    <select
                      required
                      value={formData.phoneCode}
                      onChange={(e) => setFormData(prev => ({ ...prev, phoneCode: e.target.value }))}
                      className="w-[140px] flex h-10 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#05ab95] focus:ring-2 focus:ring-[#05ab95] focus:outline-none"
                    >
                      {countryCodes.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.code} ({item.country})
                        </option>
                      ))}
                    </select>
                    <Input 
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="flex-1 border-gray-300 focus:border-[#05ab95] focus:ring-[#05ab95]"
                      placeholder="50 123 4567"
                    />
                  </div>
                </div>

                {/* Registration Days Selection */}
                <div>
                  <Label className="text-gray-700 font-medium mb-2 block">Registration *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, registrationDays: '1', selectedDate: '' }))}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.registrationDays === '1'
                          ? 'border-[#05ab95] bg-[#05ab95]/5 text-[#05ab95]'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid="reg-1day-btn"
                    >
                      <span className="block font-bold text-lg">1 Day</span>
                      <span className="block text-sm font-semibold">AED 800</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, registrationDays: '2', selectedDate: '' }))}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        formData.registrationDays === '2'
                          ? 'border-[#05ab95] bg-[#05ab95]/5 text-[#05ab95]'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid="reg-2day-btn"
                    >
                      <span className="block font-bold text-lg">Both Days</span>
                      <span className="block text-sm font-semibold">AED 1,200</span>
                    </button>
                  </div>
                </div>

                {/* Date Selection - only for 1 day */}
                {formData.registrationDays === '1' && (
                  <div>
                    <Label className="text-gray-700 font-medium mb-2 block">Select Date *</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, selectedDate: '28 June 2026' }))}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          formData.selectedDate === '28 June 2026'
                            ? 'border-[#05ab95] bg-[#05ab95]/5 text-[#05ab95]'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                        data-testid="reg-date-28-btn"
                      >
                        <span className="block font-bold">Day 1</span>
                        <span className="block text-sm">28 June 2026</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, selectedDate: '29 June 2026' }))}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          formData.selectedDate === '29 June 2026'
                            ? 'border-[#05ab95] bg-[#05ab95]/5 text-[#05ab95]'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                        data-testid="reg-date-29-btn"
                      >
                        <span className="block font-bold">Day 2</span>
                        <span className="block text-sm">29 June 2026</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Fee Display */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-gray-600">Registration Fee:</span>
                      <span className="text-gray-500 text-sm block">
                        {formData.registrationDays === '2'
                          ? 'Both Days (28-29 June 2026)'
                          : formData.selectedDate
                            ? `1 Day (${formData.selectedDate})`
                            : '1 Day — select date above'}
                      </span>
                    </div>
                    <span className="text-2xl font-bold text-[#05ab95]">
                      {formData.registrationDays === '2' ? '1,200 AED' : '800 AED'}
                    </span>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-[#05ab95] hover:bg-[#048c7a] text-white py-6 text-lg font-semibold"
                >
                  {isSubmitting ? 'Processing...' : 'Proceed to Payment'}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  You will be redirected to secure payment after clicking Proceed to Payment
                </p>
                <p className="text-xs text-gray-500 text-center mt-2">
                  In case of any issues, please email us at{' '}
                  <a href="mailto:auraconferences@gmail.com" className="text-[#05ab95] hover:underline">
                    auraconferences@gmail.com
                  </a>
                </p>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Sponsors Section */}
      <section id="sponsors" className="py-20 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              Sponsors
            </h2>
            <p className="text-center text-gray-600 mb-12">
              We thank our sponsors for their support in making AURA 2026 possible
            </p>
            
            <div className="flex flex-wrap justify-center gap-8">
              {/* B. Braun */}
              <div className="flex items-center justify-center bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow h-32 w-64">
                <img 
                  src="https://customer-assets.emergentagent.com/job_anaesthesia-conf/artifacts/lcsuyq38_bbraun.svg"
                  alt="B. Braun" 
                  className="max-h-16 object-contain"
                />
              </div>
              
              {/* GE Healthcare */}
              <div className="flex items-center justify-center bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow h-32 w-64">
                <img 
                  src="https://customer-assets.emergentagent.com/job_anaesthesia-conf/artifacts/n5vothtd_ge%20healthcare.svg"
                  alt="GE Healthcare" 
                  className="max-h-16 object-contain"
                />
              </div>

              {/* City Pharmaceutical Co. */}
              <div className="flex items-center justify-center bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow h-32 w-64">
                <img 
                  src="https://customer-assets.emergentagent.com/job_0dadb0f2-68f4-45a4-8c6b-092df69739a1/artifacts/6hyotwy9_citynew.jpg"
                  alt="City Pharmaceutical Co." 
                  className="max-h-20 object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Venue Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-3">
              Venue
            </h2>
            <p className="text-center text-gray-600 mb-10">
              InterContinental Dubai Festival City
            </p>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src="https://images.unsplash.com/photo-1708360985708-0cbad6192e04?w=800&q=80" 
                  alt="InterContinental Dubai Festival City" 
                  className="w-full h-64 md:h-80 object-cover"
                />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">InterContinental Dubai Festival City</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Located along the banks of Dubai Creek, InterContinental Dubai Festival City offers a stunning waterfront setting for AURA 2026. The hotel features world-class conference facilities and is part of the vibrant Dubai Festival City complex.
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-gray-700">
                    <MapPin className="w-4 h-4 text-[#05ab95]" />
                    <span className="text-sm">Dubai Festival City, Dubai, UAE</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar className="w-4 h-4 text-[#05ab95]" />
                    <span className="text-sm">June 28-29, 2026</span>
                  </div>
                </div>
                <Button 
                  onClick={() => window.open('https://www.google.com/maps/place/?q=place_id:ChIJjwmGh3xdXz4RkyRUy7IXAAY', '_blank')}
                  className="bg-[#05ab95] hover:bg-[#048c7a] text-white px-6 py-5 shadow-lg hover:shadow-xl transition-all"
                  data-testid="venue-map-btn"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  View on Google Maps
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_anaesthesia-conf/artifacts/kqk2p1bl_Aura2026Writinglogo.png" 
                  alt="AURA 2026 Logo" 
                  className="h-10 w-auto mb-4 brightness-0 invert"
                />
                <p className="text-sm">
                  Advanced Ultrasound Regional Anaesthesia & Pain Conference
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-3">Contact</h4>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center">
                    <Mail className="w-4 h-4 mr-2 text-[#05ab95]" />
                    auraconferences@gmail.com
                  </p>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-3">Event Details</h4>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2 text-[#05ab95]" />
                    June 28-29, 2026
                  </p>
                  <p className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-[#05ab95]" />
                    Dubai, UAE
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-800 pt-6 text-center text-sm">
              <p>Organised by Brewing Future Connect</p>
              <p className="mt-2 text-gray-400">
                In case of any issues, please email us at{' '}
                <a href="mailto:auraconferences@gmail.com" className="text-[#05ab95] hover:underline">
                  auraconferences@gmail.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;